/**
 * Workshop showcase for data frames: ChartFrame + WeatherFrame.
 *
 * Registered in workshop-routes.tsx at /workshop/shapes/data-frames.
 * Realistic mocks plus edge cases: empty points, streaming, error.
 */

import { ChartFrame, type ChartPoint } from "@/workshop/components/shapes/chart-frame/chart-frame";
import { WeatherFrame, type WeatherDay } from "@/workshop/components/shapes/weather-frame/weather-frame";

const PRICE_POINTS: ChartPoint[] = [
  { label: "Sep 5", value: 64210 },
  { label: "Sep 6", value: 65890 },
  { label: "Sep 7", value: 63120 },
  { label: "Sep 8", value: 67450 },
  { label: "Sep 9", value: 69230 },
  { label: "Sep 10", value: 68110 },
  { label: "Sep 11", value: 64980 },
  { label: "Sep 12", value: 62440 },
  { label: "Sep 13", value: 63870 },
  { label: "Sep 14", value: 66320 },
  { label: "Sep 15", value: 65190 },
  { label: "Sep 16", value: 63960 },
];

const SINGLE_POINT: ChartPoint[] = [{ label: "Sep 16", value: 63960 }];

const FORECAST_DAYS: WeatherDay[] = [
  { day: "Today", icon: "Sun", high: 23, low: 14 },
  { day: "Tue", icon: "Sun", high: 23, low: 13 },
  { day: "Wed", icon: "Cloud", high: 19, low: 12 },
  { day: "Thu", icon: "Cloud", high: 17, low: 11 },
  { day: "Fri", icon: "Zap", high: 16, low: 10 },
];

const SINGLE_DAY: WeatherDay[] = [
  { day: "Today", icon: "Sun", high: 23, low: 14 },
];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      className="mb-10"
      data-testid={`data-frames-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <h2 className="mb-4 text-sm font-semibold text-white/80">{title}</h2>
      <div className="flex flex-wrap items-start gap-6">{children}</div>
    </div>
  );
}

export function DataFramesPage() {
  return (
    <div className="workshop-canvas-bg min-h-full p-8" data-testid="data-frames-page">
      <style>{`.workshop-canvas-bg{background-color:#0f1115;background-image:linear-gradient(0deg,transparent,transparent),linear-gradient(135deg,#2b2f36 0%,#343a42 40%,#3d424a 70%,#3b2f2a 100%);position:relative}.workshop-canvas-bg::before{content:"";position:absolute;inset:0;z-index:0;pointer-events:none;opacity:0.1;background:radial-gradient(circle at 22% 18%,rgba(156,132,255,0.18),transparent 58%),radial-gradient(circle at 72% 70%,rgba(111,168,198,0.12),transparent 60%)}.workshop-canvas-bg>*{position:relative;z-index:1}`}</style>
      <div className="mb-8">
        <h1 className="text-lg font-bold text-white">Data Frames</h1>
        <p className="mt-1 text-sm text-white/40">
          Chart trend and weather forecast frames. Bars are interactive.
        </p>
      </div>

      <Section title="Chart — Default">
        <div className="w-[380px]">
          <ChartFrame
            data-testid="chart-frame-default"
            title="BTC / USD — 12d"
            insight="lowest in 30d"
            points={PRICE_POINTS}
            unit="$"
            status="done"
          />
        </div>
        <div className="w-[380px]">
          <ChartFrame
            data-testid="chart-frame-single"
            title="ETH / USD — 1d"
            points={SINGLE_POINT}
            unit="$"
            status="done"
          />
        </div>
      </Section>

      <Section title="Chart — Edge Cases">
        <div className="w-[380px]">
          <ChartFrame
            data-testid="chart-frame-empty"
            title="SOL / USD — 12d"
            points={[]}
            unit="$"
            status="done"
          />
        </div>
        <div className="w-[380px]">
          <ChartFrame
            data-testid="chart-frame-streaming"
            title="BTC / USD — live"
            points={[]}
            unit="$"
            status="streaming"
          />
        </div>
        <div className="w-[380px]">
          <ChartFrame
            data-testid="chart-frame-error"
            title="BTC / USD — 12d"
            points={[]}
            unit="$"
            status="error"
          />
        </div>
      </Section>

      <Section title="Weather — Default">
        <div className="w-[320px]">
          <WeatherFrame
            data-testid="weather-frame-default"
            place="Berlin"
            temp={21}
            condition="Mostly Sunny"
            high={23}
            low={14}
            days={FORECAST_DAYS}
            status="done"
          />
        </div>
      </Section>

      <Section title="Weather — Single Day">
        <div className="w-[320px]">
          <WeatherFrame
            data-testid="weather-frame-single"
            place="Berlin"
            temp={21}
            condition="Mostly Sunny"
            high={23}
            low={14}
            days={SINGLE_DAY}
            status="done"
          />
        </div>
        <div className="w-[320px]">
          <WeatherFrame
            data-testid="weather-frame-today-only"
            place="Berlin"
            temp={21}
            condition="Mostly Sunny"
            high={23}
            low={14}
            days={[]}
            status="done"
          />
        </div>
      </Section>

      <Section title="Weather — Edge Cases">
        <div className="w-[320px]">
          <WeatherFrame
            data-testid="weather-frame-streaming"
            place="Berlin"
            temp={21}
            condition="Mostly Sunny"
            high={23}
            low={14}
            days={[]}
            status="streaming"
          />
        </div>
        <div className="w-[320px]">
          <WeatherFrame
            data-testid="weather-frame-error"
            place="Reykjavík with a very long place name"
            temp={-2}
            condition="Light snow showers"
            high={0}
            low={-6}
            days={[]}
            status="error"
          />
        </div>
      </Section>
    </div>
  );
}
